import { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Loader2, Camera, Upload, Trash2, RefreshCw, ImageIcon } from "lucide-react";
import { api, API } from "../lib/config";
import { toast } from "sonner";

// iter183 — Upload every photo via /api/media/upload (Emergent Object Storage).
// The server handles auto-compression + stores both original + optimized copies,
// so we no longer resize on the client. We keep it simple: pick from gallery
// or camera, upload with progress, retry on failure, cap at 5 per kind.

const MAX_PER_KIND = 5;
const MAX_RETRIES = 3;

const wait = (ms) => new Promise((r) => setTimeout(r, ms));

async function uploadOne(file, onProgress) {
  const form = new FormData();
  form.append("file", file, file.name || "photo.jpg");
  let lastErr;
  for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
    try {
      const { data } = await api.post("/media/upload", form, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (evt) => {
          if (evt.total && onProgress) onProgress(Math.round((evt.loaded / evt.total) * 100));
        },
      });
      return data;
    } catch (e) {
      lastErr = e;
      // Fail fast on 4xx (except 5xx/timeout). Retry only on network/5xx.
      const status = e?.response?.status;
      if (status && status >= 400 && status < 500) throw e;
      if (attempt < MAX_RETRIES) await wait(600 * attempt);
    }
  }
  throw lastErr;
}

function photoSrc(photo) {
  // New photos: `url = "/api/media/{id}"` — prefix with API base for cross-origin fetch.
  if (photo?.media_id) return `${API}/media/${photo.media_id}`;
  if (photo?.url && photo.url.startsWith("/api/")) return `${API.replace(/\/api$/, "")}${photo.url}`;
  // Legacy: base64 data URL.
  return photo?.data || "";
}

export default function PhotosUploadDialog({ open, onOpenChange, booking, onUpdated }) {
  const [active, setActive] = useState("before"); // before | after
  const [photos, setPhotos] = useState({ before: [], after: [] });
  const [queue, setQueue] = useState([]); // [{ id, name, progress, status, error }]
  const [busy, setBusy] = useState(false);
  const galleryRef = useRef(null);
  const cameraRef = useRef(null);

  useEffect(() => {
    if (!open || !booking) return;
    setPhotos({ before: booking.before_photos || [], after: booking.after_photos || [] });
    setQueue([]);
  }, [open, booking]);

  const remaining = MAX_PER_KIND - (photos[active]?.length || 0);

  const doUpload = async (files) => {
    if (!files || !files.length) return;
    if (files.length > remaining) {
      toast.error(`Only ${remaining} more ${active} photo${remaining === 1 ? "" : "s"} can be added.`);
      files = Array.from(files).slice(0, remaining);
    }
    setBusy(true);
    const items = Array.from(files).map((f, i) => ({
      id: `${Date.now()}-${i}`, file: f, name: f.name || `photo-${i + 1}.jpg`,
      progress: 0, status: "queued", error: null,
    }));
    setQueue(items);
    let successCount = 0;
    for (const item of items) {
      setQueue((q) => q.map((x) => x.id === item.id ? { ...x, status: "uploading" } : x));
      try {
        const desc = await uploadOne(item.file, (p) =>
          setQueue((q) => q.map((x) => x.id === item.id ? { ...x, progress: p } : x))
        );
        // Attach to the booking
        const { data: out } = await api.post(`/admin/bookings/${booking.booking_id}/photos`, {
          kind: active, media_id: desc.media_id, caption: null,
        });
        setQueue((q) => q.map((x) => x.id === item.id ? { ...x, status: "done", progress: 100 } : x));
        setPhotos((p) => ({
          ...p,
          [active]: [...p[active], {
            photo_id: out.photo_id, media_id: desc.media_id, kind: active,
            url: `/api/media/${desc.media_id}`,
          }],
        }));
        successCount += 1;
      } catch (e) {
        setQueue((q) => q.map((x) => x.id === item.id ? {
          ...x, status: "failed", error: e?.response?.data?.detail || e.message || "Upload failed"
        } : x));
      }
    }
    if (successCount > 0) {
      toast.success(`${successCount} ${active} photo${successCount === 1 ? "" : "s"} uploaded`);
      onUpdated && onUpdated();
    }
    setBusy(false);
    if (galleryRef.current) galleryRef.current.value = "";
    if (cameraRef.current) cameraRef.current.value = "";
  };

  const retryFailed = async (item) => {
    setQueue((q) => q.map((x) => x.id === item.id ? { ...x, status: "uploading", error: null, progress: 0 } : x));
    try {
      const desc = await uploadOne(item.file, (p) =>
        setQueue((q) => q.map((x) => x.id === item.id ? { ...x, progress: p } : x))
      );
      const { data: out } = await api.post(`/admin/bookings/${booking.booking_id}/photos`, {
        kind: active, media_id: desc.media_id,
      });
      setQueue((q) => q.map((x) => x.id === item.id ? { ...x, status: "done", progress: 100 } : x));
      setPhotos((p) => ({
        ...p,
        [active]: [...p[active], {
          photo_id: out.photo_id, media_id: desc.media_id, kind: active,
          url: `/api/media/${desc.media_id}`,
        }],
      }));
      onUpdated && onUpdated();
    } catch (e) {
      setQueue((q) => q.map((x) => x.id === item.id ? {
        ...x, status: "failed", error: e?.response?.data?.detail || e.message || "Retry failed"
      } : x));
    }
  };

  const removePhoto = async (photo) => {
    if (!window.confirm("Delete this photo?")) return;
    try {
      await api.delete(`/admin/bookings/${booking.booking_id}/photos/${photo.photo_id}`);
      setPhotos((p) => ({ ...p, [active]: p[active].filter((x) => x.photo_id !== photo.photo_id) }));
      onUpdated && onUpdated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  if (!booking) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="photos-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Camera className="w-5 h-5 text-[#047857]" /> Before / After Photos · {booking.name}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 mt-1">
          <div className="flex items-center gap-2" data-testid="photos-tabs">
            {["before", "after"].map((k) => (
              <button key={k} type="button" onClick={() => setActive(k)}
                data-testid={`photos-tab-${k}`}
                className={`px-3 py-1.5 rounded-full text-[12px] font-extrabold uppercase tracking-[0.14em] ${active === k ? "bg-[#062818] text-white" : "bg-gray-100 text-gray-700"}`}>
                {k} ({photos[k].length}/{MAX_PER_KIND})
              </button>
            ))}
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 min-h-[120px]" data-testid={`photos-grid-${active}`}>
            {photos[active].length === 0 ? (
              <div className="col-span-full text-center text-xs text-gray-500 py-6 border-2 border-dashed border-gray-200 rounded-xl">
                No {active} photos yet.
              </div>
            ) : photos[active].map((p) => (
              <div key={p.photo_id} className="relative rounded-xl overflow-hidden group bg-gray-100 aspect-square" data-testid={`photo-${p.photo_id}`}>
                <img src={photoSrc(p)} alt={p.caption || ""} className="w-full h-full object-cover" loading="lazy" />
                <button onClick={() => removePhoto(p)} className="absolute top-1.5 right-1.5 w-7 h-7 rounded-full bg-rose-600/90 text-white hover:bg-rose-600 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`photo-delete-${p.photo_id}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>

          {/* Live upload progress */}
          {queue.length > 0 && (
            <div className="space-y-1.5" data-testid="photos-upload-queue">
              {queue.map((item) => (
                <div key={item.id} className="flex items-center gap-2 text-xs">
                  <div className="flex-1 truncate">{item.name}</div>
                  {item.status === "uploading" && (
                    <div className="flex items-center gap-2 min-w-[110px]">
                      <div className="flex-1 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                        <div className="h-full bg-emerald-500 transition-all" style={{ width: `${item.progress}%` }} />
                      </div>
                      <span className="tabular-nums text-gray-600">{item.progress}%</span>
                    </div>
                  )}
                  {item.status === "done" && <span className="text-emerald-700 font-bold">Uploaded</span>}
                  {item.status === "failed" && (
                    <>
                      <span className="text-rose-700 font-bold truncate max-w-[140px]" title={item.error}>Failed</span>
                      <button onClick={() => retryFailed(item)} className="text-emerald-700 font-extrabold underline flex items-center gap-1" data-testid={`retry-${item.id}`}>
                        <RefreshCw className="w-3 h-3" /> Retry
                      </button>
                    </>
                  )}
                  {item.status === "queued" && <span className="text-gray-500">Queued…</span>}
                </div>
              ))}
            </div>
          )}

          <input ref={galleryRef} type="file" multiple accept="image/*" className="hidden"
                 onChange={(e) => doUpload(e.target.files)} data-testid="photos-file-input" />
          <input ref={cameraRef} type="file" accept="image/*" capture="environment" className="hidden"
                 onChange={(e) => doUpload(e.target.files)} data-testid="photos-camera-input" />

          <div className="grid grid-cols-2 gap-2">
            <Button onClick={() => cameraRef.current?.click()} disabled={busy || remaining <= 0}
              data-testid="photos-camera-btn"
              variant="outline"
              className="rounded-full font-extrabold border-2 border-[#047857] text-[#047857] hover:bg-emerald-50">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Camera className="w-4 h-4 mr-2" /> Take photo</>}
            </Button>
            <Button onClick={() => galleryRef.current?.click()} disabled={busy || remaining <= 0}
              data-testid="photos-upload-btn"
              className="rounded-full bg-[#047857] hover:bg-[#065F46] text-white font-extrabold">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><ImageIcon className="w-4 h-4 mr-2" /> Choose from gallery</>}
            </Button>
          </div>
          {remaining <= 0 && (
            <div className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded-xl p-2 text-center font-bold" data-testid="photos-max-warning">
              Maximum {MAX_PER_KIND} {active} photos reached. Remove one to add another.
            </div>
          )}
          <div className="text-[11px] text-gray-500 text-center">
            Photos are auto-compressed on upload — no size limit under {25} MB. Originals are archived.
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
